import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { sendOnboardingResponse } from '@/src/renderer/lib/onboarding/onboardingResponse'
import { withTimeout } from '@/src/renderer/lib/withTimeout'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { PinboardIcon } from '@/src/renderer/components/icons/PinboardIcon'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import type { UserIntent, UserIntentOption } from './SetupIntentPage.types'
import { CardKeysHint } from '../../../components/CardKeysHint'
import './SetupIntentPage.scss'

/**
 * Available user intent options
 * 12 options in a 2x6 grid layout
 */
const USER_INTENT_OPTIONS: UserIntentOption[] = [
  { id: 'daily_routines', emoji: '🗓️', labelKey: 'setupIntent.dailyRoutines', bgColor: '#FFE4E6' },
  { id: 'procrastination', emoji: '🚀', labelKey: 'setupIntent.procrastination', bgColor: '#DBEAFE' },
  { id: 'task_planning', emoji: '📝', labelKey: 'setupIntent.taskPlanning', bgColor: '#D6E7AF' },
  { id: 'focus', emoji: '🪷', labelKey: 'setupIntent.focus', bgColor: '#EDE9FE' },
  { id: 'project_organization', emoji: '🗂️', labelKey: 'setupIntent.projectOrganization', bgColor: '#FFE4B5' },
  { id: 'priority_setting', emoji: '⭐', labelKey: 'setupIntent.prioritySetting', bgColor: '#E0E7FF' },
  { id: 'goal_tracking', emoji: '🎯', labelKey: 'setupIntent.goalTracking', bgColor: '#EDE9FE' },
  { id: 'motivation_rewards', emoji: '🏆', labelKey: 'setupIntent.motivationRewards', bgColor: '#FCE7F3' },
  { id: 'time_management', emoji: '⏱️', labelKey: 'setupIntent.timeManagement', bgColor: '#FEF3C7' },
  { id: 'build_habits', emoji: '🔄', labelKey: 'setupIntent.buildHabits', bgColor: '#E0F2FE' },
  { id: 'energy_breaks', emoji: '☕', labelKey: 'setupIntent.energyBreaks', bgColor: '#EDE9FE' },
  { id: 'minimize_distractions', emoji: '🚫', labelKey: 'setupIntent.minimizeDistractions', bgColor: '#FEE2E2' },
]

/**
 * SetupIntentPage Component
 *
 * Second onboarding questionary page that asks users about their specific goals.
 * Displays a 2x6 grid of selectable intent cards with keyboard navigation.
 *
 * @remarks
 * Features:
 * - 12 selectable intent cards in 2-column grid
 * - Full keyboard navigation support
 * - Skip button (ESC) finishes setup without saving intents
 * - Navigates directly to /home after completion
 */
export default function SetupIntentPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const account = useAccountSource()
  const location = useLocation()
  const platformAPI = getPlatformAPI()

  // Get saved data from navigation state (passed from purpose page)
  const locationState = location.state as {
    savedPurposes?: any[]
    savedIntents?: UserIntent[]
    profileData?: { fullName: string; password: string }
  }
  const savedPurposesFromState = locationState?.savedPurposes
  const savedIntentsFromState = locationState?.savedIntents
  const profileDataFromState = locationState?.profileData

  // State - Initialize with savedIntents from state if available
  const [selectedIntents, setSelectedIntents] = useState<UserIntent[]>(savedIntentsFromState || [])
  const [focusedIndex, setFocusedIndex] = useState(0)
  /*
    Ob schon in die Karten geklickt wurde (#147). Bis dahin trägt die Karte mit Fokus ihren Ring
    immer — wer per Mausklick auf die Seite kam, sah sonst nicht, dass die Tastatur schon in den
    Karten steht (`:focus-visible` greift nach einem Klick nicht). Danach nur noch per Tastatur,
    damit eine angeklickte Karte keinen zweiten Rahmen bekommt.
  */
  const [pointerUsed, setPointerUsed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userName, setUserName] = useState<string>('')

  // Refs
  const firstCardRef = useRef<HTMLButtonElement>(null)

  // Platform class
  const platformClass = platformAPI.platform === 'electron' ? 'setup-intent-page--electron' : 'setup-intent-page--web'

  /**
   * Load user name from profileData or supabase
   */
  useEffect(() => {
    const loadUserName = async () => {
      // First try to get name from profileData (if coming from previous pages)
      if (profileDataFromState?.fullName) {
        setUserName(profileDataFromState.fullName)
      } else {
        // Otherwise load from the account
        try {
          const user = await account.getCloudUser()
          setUserName(user?.fullName || '')
        } catch (error) {
          console.error('[SetupIntent] Error loading user name:', error)
        }
      }
    }

    loadUserName()
  }, [profileDataFromState, account])

  /**
   * Auto-focus on first card
   */
  useEffect(() => {
    // Track page view (only for cloud users)
    const loginMethod = localStorage.getItem('loginMethod')
    if (loginMethod !== 'offline') {
      void account.updateSetupProgress('intent')
    }

    if (firstCardRef.current) {
      firstCardRef.current.focus()
    }
  }, [account])

  /**
   * Toggle intent selection
   */
  const toggleIntent = (intentId: UserIntent) => {
    setSelectedIntents(prev => {
      if (prev.includes(intentId)) {
        return prev.filter(id => id !== intentId)
      } else {
        return [...prev, intentId]
      }
    })
  }

  /**
   * Keyboard navigation for 2x6 grid
   */
  const handleCardKeyDown = (e: React.KeyboardEvent, intentId: UserIntent, index: number) => {
    const COLUMNS = 2

    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      toggleIntent(intentId)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleContinue()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      const nextIndex = (index + 1) % USER_INTENT_OPTIONS.length
      setFocusedIndex(nextIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-intent-page__card')[nextIndex]?.focus()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevIndex = (index - 1 + USER_INTENT_OPTIONS.length) % USER_INTENT_OPTIONS.length
      setFocusedIndex(prevIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-intent-page__card')[prevIndex]?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = (index + COLUMNS) % USER_INTENT_OPTIONS.length
      setFocusedIndex(nextIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-intent-page__card')[nextIndex]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = (index - COLUMNS + USER_INTENT_OPTIONS.length) % USER_INTENT_OPTIONS.length
      setFocusedIndex(prevIndex)
      document.querySelectorAll<HTMLButtonElement>('.setup-intent-page__card')[prevIndex]?.focus()
    }
  }

  /**
   * Handles continue button click: saves the selected intents and finishes setup
   */
  const handleContinue = () => finishSetup(selectedIntents)

  /**
   * Skip the question: finishes setup like "Continue", but saves no intents.
   */
  const handleSkip = () => finishSetup(null)

  /**
   * Completes the setup flow and moves on to onboarding.
   * @param intents - intents to save, or `null` when the question was skipped
   */
  const finishSetup = async (intents: UserIntent[] | null) => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      // Save intents to database (cloud-sync mode only)
      if (intents) {
        const user = await account.getCloudUser()

        if (user) {
          const result = await account.saveIntentions(user.id, {
            userIntents: intents
          })

          if (!result.success) {
            console.error('[SetupIntent] Save error:', result.error)
          }
        } else {
          console.warn('[SetupIntent] No authenticated user found')
        }
      }

      // Setup flow completed
      const loginMethod = localStorage.getItem('loginMethod')
      const isOfflineMode = loginMethod === 'offline'

      if (isOfflineMode) {
        // Offline mode - save to Dexie and mark setup as completed
        if (intents) {
          try {
            // Update existing offline profile with intents
            const updated = await account.updateOfflineProfile({
              userIntents: intents,
              updatedAt: new Date(),
              lastModified: new Date()
            })
            if (!updated) {
              console.warn('[SetupIntent] No offline profile found')
            }
          } catch (error) {
            console.error('[SetupIntent] Error saving to Dexie:', error)
          }
        }

        localStorage.setItem('offlineSetupCompleted', 'true')

        // Die Antworten gehen auch ohne Konto raus — anonym, ohne Kennung
        // (25.09.2026). Höchstens 1,5 s warten: Was bis zum Fensterwechsel nicht
        // durch ist, geht beim nächsten Start nach (`onboardingResponse.ts`).
        await withTimeout(
          sendOnboardingResponse(row => account.submitAnonymousOnboarding(row), {
            purposes: (savedPurposesFromState ?? []) as string[],
            intents: intents ?? [],
            locale: i18n.language ?? 'de',
          }),
          1500,
          undefined,
        )
      }

      // Remove loginMethod flag (for OTP/password flows)
      localStorage.removeItem('loginMethod')

      // Use IPC to transition to separate onboarding window
      if (window.api) {
        await window.api.invoke('transition-to-onboarding')
      } else {
        navigate('/onboarding')
      }
    } catch (error) {
      console.error('[SetupIntent] Unexpected error:', error)
      // Even on error, remove loginMethod flag to prevent redirect loop
      localStorage.removeItem('loginMethod')
      // Use IPC to transition to separate onboarding window
      if (window.api) {
        await window.api.invoke('transition-to-onboarding')
      } else {
        navigate('/onboarding')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Go back to previous page
   */
  const handleBack = () => {
    navigate('/setup-purpose', {
      state: {
        savedPurposes: savedPurposesFromState,
        savedIntents: selectedIntents,
        profileData: profileDataFromState
      }
    })
  }

  return (
    <div
      className={`setup-intent-page ${platformClass}${pointerUsed ? ' setup-intent-page--pointer' : ''}`}
      // Escape überspringt die Frage, wo auch immer der Fokus steht — das
      // ESC-Kürzel am Überspringen-Knopf verspricht genau das.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          handleSkip()
        }
      }}
    >
      {/* Scrollable Container */}
      <div className="setup-intent-page__scrollable-container">
        <div className="setup-intent-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-intent-page__content-area">

            {/* Left Side */}
            <div className="setup-intent-page__left-section">
              <div className="setup-intent-page__left-content">
                <div className="setup-intent-page__icon">
                  <PinboardIcon />
                </div>
                <h1 className="setup-intent-page__title">
                  {t('setupIntent.title', { name: userName })}
                </h1>
                <p className="setup-intent-page__subtitle">
                  {t('setupIntent.subtitle')}
                </p>
              </div>
            </div>

            {/* Right Side */}
            <div className="setup-intent-page__right-section">
              <p className="setup-intent-page__prompt">
                {selectedIntents.length > 0
                  ? t('setupIntent.selected', { count: selectedIntents.length })
                  : t('setupIntent.selectPrompt')}
              </p>

              {/* Grid of Cards */}
              <div className="setup-intent-page__grid" onPointerDown={() => setPointerUsed(true)}>
                {USER_INTENT_OPTIONS.map((option, index) => {
                  const isSelected = selectedIntents.includes(option.id)
                  const isFocused = focusedIndex === index

                  return (
                    <button
                      key={option.id}
                      ref={index === 0 ? firstCardRef : null}
                      onClick={() => toggleIntent(option.id)}
                      onKeyDown={(e) => handleCardKeyDown(e, option.id, index)}
                      onFocus={() => setFocusedIndex(index)}
                      className={`setup-intent-page__card ${
                        isSelected ? 'setup-intent-page__card--selected' : ''
                      } ${
                        isFocused ? 'setup-intent-page__card--focused' : ''
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className="setup-intent-page__card-emoji">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M29.96 2.27C28.26 0 20.13 0 20.13 0H16H11.87C11.87 0 3.74 0 2.04 2.27C0.41 4.45 0 7.14 0 16C0 24.86 0.41 27.55 2.04 29.73C3.74 32 11.87 32 11.87 32H16H20.13C20.13 32 28.26 32 29.96 29.73C31.59 27.55 32 24.86 32 16C32 7.14 31.59 4.45 29.96 2.27Z" fill={option.bgColor}/>
                        </svg>
                        <span className="setup-intent-page__card-emoji-icon">{option.emoji}</span>
                      </span>
                      <span className="setup-intent-page__card-label">
                        {t(option.labelKey)}
                      </span>
                    </button>
                  )
                })}
              </div>
              <CardKeysHint />
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="setup-intent-page__action-bar">
        <Button
          variant="tertiary"
          onClick={handleSkip}
          shortcut={{ kind: 'character', text: 'ESC' }}
          disabled={isSubmitting}
        >
          {t('setupIntent.skip')}
        </Button>
        <div className="setup-intent-page__action-bar-content">
          <Button
            variant="secondary"
            onClick={handleBack}
          >
            {t('setupIntent.back')}
          </Button>
          <Button
            onClick={handleContinue}
            shortcut={{ kind: 'return' }}
            disabled={isSubmitting}
          >
            {t('setupIntent.continue')}
          </Button>
        </div>
      </div>
    </div>
  )
}
